<template>
    <h1 v-if="!loading">
        <span class="u-display-block">{{ courseData.courseTitle }}</span>
        <span class="module-header-subtitle module-header-subtitle-light">{{ courseData.subject + " " + courseData.catalogNumber }}</span>
    </h1>

    <div v-if="loading">
        <p>Loading course details...</p>
    </div>

    <div v-else-if="!loading && error">
        <p>Error loading course data: {{ error.message }}</p>
        <p>Please check the console for more details.</p>
    </div>
    <template v-else-if="!loading">
        <div :class="hasSidebar ? 'o-sidebar-grid' : null">
            <div v-if="!courseData || Object.keys(courseData).length === 0">
                <p>No course data available.</p>
            </div>
            <template v-else>
                <div :class="hasSidebar ? 'o-sidebar-grid__col o-sidebar-grid__col--main-content' : null">
                        <div class="o-flex-box u-space-gap u-space-block-end">
                            <div><p><b>Subject:</b> {{ courseData.departmentName }}</p></div>
                            <div><p><b>Credit Hours:</b> {{ courseData.credit }}</p></div>
                        </div>
                        <p><b>Course Description:</b> {{ courseData.courseDescription }}</p>
                        <p v-if="courseData.preRequisite != ''"><b>Prerequisites:</b> {{ courseData.preRequisite }}</p>

                        <div>
                            <p v-if="courseData.isIAIGenEd"><b>IAI Gen Ed Category:</b> {{ courseData.iaiGenedDescription }}</p>
                            <p v-if="courseData.isGened"><b>ISU Gen Ed Category:</b> {{ courseData.genEdIdentifier }}</p>
                            <p v-if="courseData.isIAIMajor"><b>IAI Major Category:</b> {{ courseData.iaiMajorDescription }}</p>
                            <p v-if="courseData.isBSMT"><b>Graduation Requirement:</b> Science, Mathematics and Technology [SMT]</p>
                            <p v-if="courseData.isAmali"><b>Graduation Requirement:</b> Asia, the Middle East, Africa, Latin America, or Indigenous Peoples of the World (AMALI)</p>
                            <p v-if="courseData.isWorldLanguage"><b>Graduation Requirement: </b>World Languages</p>
                        </div>

                        <div v-if="courseData.isIAI || courseData.isIAIGenEd || courseData.isIAIMajor || courseData.isGened || courseData.isAmali || courseData.isIdeas || courseData.isBSMT" class="u-display-flex u-flex-flow-row-wrap u-space-gap-md u-space-block">
                            <p class="flag-card-detail u-space-block-end-collapsed" v-if="courseData.isIAIGenEd">IAI GenEd</p>
                            <p class="flag-card-detail u-space-block-end-collapsed" v-if="courseData.isIAIMajor">IAI Major</p>
                            <p class="flag-card-detail u-space-block-end-collapsed" v-if="courseData.isGened">GenEd</p>
                            <p class="flag-card-detail u-space-block-end-collapsed" v-if="courseData.isAmali">AMALI</p>
                            <p class="flag-card-detail u-space-block-end-collapsed" v-if="courseData.isIdeas">IDEAS</p>
                            <p class="flag-card-detail u-space-block-end-collapsed" v-if="courseData.isBSMT">BSMT</p>
                        </div>

                        <p class="u-font-size-copy-sm" v-if="courseData.isIdeas"><em><b>IDEAS</b> - Inclusion, Diversity, Equity, and Access in U.S. Society. This graduation requirement is for all students, starting with students following the 2023-2024 catalog.</em></p>
                </div>
                <div v-if="hasSidebar" class="o-sidebar-grid__col o-sidebar-grid__col--side-content">
                    <div class="c-box u-space-block-loose" v-if="!courseData.crossListedCourses || courseData.crossListedCourses.length !== 0">
                        <h2 class="u-font-size-copy-xl">Equivilant Courses</h2>
                        <ul class="o-unstyled-list" v-for="(course, courseIndex) in courseData.crossListedCourses">
                            <li><a :href="'/'+course.subject.toLowerCase()+'/'+course.catalogNumber.toLowerCase()+'/'">{{ course.subject }} {{ course.catalogNumber }}</a> - {{ course.courseTitle }}</li>
                        </ul>
                    </div>
                </div>
            </template>
        </div>
    
      
        <ISUAlert v-if="!courseData.futureCatalogCourseChanges || courseData.futureCatalogCourseChanges.length !== 0" class="u-space-inline-spacious@lg u-space-block-start-loose u-space-block-end-expanded">
            <h2 class="u-font-size-copy-xl">Upcoming Course Changes</h2>
            <template v-for="(change, changeIndex) in courseData.futureCatalogCourseChanges">
                <ul class="o-unstyled-list">
                    <li>Date: {{ getDate(change.effectiveDate.date) }}</li>
                    <ul class="o-unstyled-list" v-for="(singleChange, singleChangeKey) in change.changes">
                        <li>{{ singleChange.type }} changes from <b>{{ singleChange.from }}</b> to <b>{{ singleChange.to }}</b></li>
                    </ul>
                </ul>
            </template>
        </ISUAlert>

        <div class="c-alert c-alert--no-results u-space-block" v-if="!courseData.courseOfferings || courseData.courseOfferings.length === 0">
            <div class="c-alert__content">
                <p class="c-alert__copy">No Courses Scheduled</p>
            </div>
        </div>

        <div v-for="(semester, index) in courseData.courseOfferings">
            <section :aria-labelledby="generateSectionId(semester.termInformation.termDescription)">
                <h2 :id="generateSectionId(semester.termInformation.termDescription)">{{ semester.termInformation.termDescription }}</h2>
                <ISUAccordionDetailsGroup :name="semester.termInformation.termDescription.replace(/\s+/g, '-').toLowerCase()" class="u-space-block-end">
                    <ISUAccordionDetails
                        v-for="(courseOffering, coIndex) in semester.termOfferings"
                        :key="`${semester.termInformation.termDescription.replace(/\s+/g, '-').toLowerCase()}-section-${courseOffering.section}`"
                        :id="`${semester.termInformation.termDescription.replace(/\s+/g, '-').toLowerCase()}-section-${courseOffering.section}`"
                    >
                        <template #summary>
                            <div class="u-width-100">
                                <div class="course-intro u-align-items-start u-space-block-end-tight">
                                    <div><h3><span class="lecture-header-subtitle-light">Section {{ courseOffering.section }}</span> - {{ courseOffering.component }}</h3></div>
                                    <div class="course-modality"
                                        v-if="courseOffering.instructionMode === 'Hybrid'"
                                        style="--bg-color: var(--ilstu-purple-21); margin-right:3em;"
                                        >
                                        {{ courseOffering.instructionMode }}
                                        </div>

                                        <div class="course-modality"
                                        v-else-if="courseOffering.instructionMode === 'In Person'"
                                        style="--bg-color: var(--ilstu-coral-21); margin-right:3em;"
                                        >
                                        {{ courseOffering.instructionMode }}
                                        </div>

                                        <div class="course-modality"
                                        v-else-if="courseOffering.instructionMode === 'Additional Location'"
                                        style="--bg-color: var(--ilstu-magenta-21); margin-right:3em;"
                                        >
                                        {{ courseOffering.instructionMode }}
                                        </div>

                                        <div class="course-modality"
                                        v-else-if="courseOffering.instructionMode === 'Internet/Online'"
                                        style="--bg-color: var(--ilstu-azul-21); margin-right:3em;"
                                        >
                                        {{ courseOffering.instructionMode }}
                                        </div>

                                        <div class="course-modality"
                                        v-else-if="courseOffering.instructionMode === 'Online/Hybrid'"
                                        style="--bg-color: var(--ilstu-azul-19); margin-right:3em;"
                                        >
                                        {{ courseOffering.instructionMode }}
                                        </div>

                                        <div class="course-modality"
                                        v-else
                                        style="--bg-color: var(--ilstu-verde-21); margin-right:3em;"
                                        >
                                        {{ courseOffering.instructionMode }}
                                    </div>
                                </div>
                                <div class="course-outro o-flex-box u-space-gap-col u-space-gap-row-sm u-flex-flow-row-wrap u-space-block-start-tight">
                                    <div v-for="(instructor, instIndex) in courseOffering.instructors" class="o-flex-box u-space-gap-xs">
                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">frame_person</span>
                                        <p class="u-font-size-copy-xs u-font-weight-regular">{{ instructor.firstName }} {{ instructor.lastName }}</p>
                                    </div>
                                    <div v-if="!courseOffering.meetingPatterns[0].isTBD || !courseOffering.meetingPatterns[0].isTBD == null" class="o-flex-box u-space-gap-xs">
                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">calendar_clock</span>
                                        <p class="u-font-size-copy-xs u-font-weight-regular">
                                            <span v-if="courseOffering.meetingPatterns.length == 1">
                                                <span v-if="courseOffering.meetingPatterns[0].meetingDays != ''">{{ formatMeetingDays(courseOffering.meetingPatterns[0].meetingDays) }} {{ formatMeetingTime(courseOffering.meetingPatterns[0].startTime + '-' + courseOffering.meetingPatterns[0].endTime) }}&nbsp;</span>
                                            </span>
                                            <span v-else>Multiple Meeting Times</span>
                                        </p>
                                    </div>
                                    <div v-if="!courseOffering.isZeroMaxEnrollment || !courseOffering.isZeroMaxEnrollment == null" class="o-flex-box u-space-gap-xs">
                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">people</span>
                                        <p class="u-font-size-copy-xs u-font-weight-regular">Enrollment: {{ courseOffering.currentEnrollment }} / {{ courseOffering.maxEnrollment }}</p>
                                    </div>
                                    <div class="o-flex-box u-space-gap-xs">
                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">bookmark_check</span>
                                        <p class="u-font-size-copy-xs u-font-weight-regular">Credits: {{ courseOffering.credits }}</p>
                                    </div>
                                    <div v-if="courseOffering.isMajorOnly || courseOffering.isHonorsOnly || courseOffering.isFreshmanOnly" class="o-flex-box u-space-gap-xs">
                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">school</span>
                                        <p class="u-font-size-copy-xs u-font-weight-regular">
                                            <span v-if="courseOffering.isMajorOnly">{{ courseOffering.classAttributes.MAJORONLYMAJ.description }}</span>
                                            <span v-if="courseOffering.isHonorsOnly">{{ courseOffering.classAttributes.HONORSHON.description }}</span>
                                            <span v-if="courseOffering.isFreshmanOnly">{{ courseOffering.classAttributes.FRESHONLYFRSH.description }}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </template>
                            <h4 class="u-space-block-end-collapsed">Meeting Times:</h4>
                            <div class="o-card-grid o-fixed-grid o-fixed-grid--two-up u-space-block-end-loose">
                                <div v-for="(meetingPattern, mpIndex) in courseOffering.meetingPatterns">
                                   <h5 class="u-space-block-end-collapsed" v-if="courseOffering.meetingPatterns.length > 1 && meetingPattern.isHybrid">Hybrid Meeting:</h5>
                                    <h5 class="u-space-block-end-collapsed" v-else-if="courseOffering.meetingPatterns.length > 1 && meetingPattern.isOnline">Online Meeting:</h5>
                                    <h5 class="u-space-block-end-collapsed" v-else-if="courseOffering.meetingPatterns.length > 1 && meetingPattern.isOnlineAndHybrid">Online/Hybrid Meeting:</h5>
                                    <h5 class="u-space-block-end-collapsed" v-else-if="courseOffering.meetingPatterns.length > 1 && (courseOffering.buildingAbbreviation !== '' || courseOffering.buildingAbbreviation !== null)">In Person Meeting:</h5>

                                    <p v-if="meetingPattern.isTBD" class="u-font-size-copy-sm">Time: Not yet determined</p>
                                    <p v-else class="u-font-size-copy-sm">
                                        {{ getDate(meetingPattern.startDate) + " - " + getDate(meetingPattern.endDate)}}<br>
                                    <span>{{ meetingPattern.meetingDays }}, {{ formatMeetingTime(meetingPattern.startTime) + " - " + formatMeetingTime(meetingPattern.endTime)}}</span><br>
                                    <span v-if="meetingPattern.buildingAbbreviation !== ''"><a :href="'https://maps.illinoisstate.edu?loc='+ meetingPattern.buildingAbbreviation" class="c-action-link">{{ meetingPattern.buildingFullName }} {{ meetingPattern.buildingRoomNumber }}</a></span></p>

                                </div>
                            </div>

                            <p class="u-font-size-copy-sm" v-if="courseOffering.classNotes.predefinedNotes[0] || courseOffering.classNotes.instructorNotes[0]"><b>Class Notes:</b> {{ courseOffering.classNotes.predefinedNotes[0] }} {{ courseOffering.classNotes.instructorNotes[0] }}</p>
                            
                            <p v-if="courseOffering.isZeroMaxEnrollment"><b>Current Enrollment:</b> unavailable, speak with your academic advisor</p>
                                
                            <p class="u-font-size-copy-sm" v-if="courseOffering.hasNoMaterials"><b>Materials:</b> This section has no materials</p>
                               

                                <div v-else-if="courseOffering.materialsFinalized">
                                    <h4 class="u-space-block-end-collapsed">Class Materials:</h4>
                                    <div v-for="(material, matIndex) in courseOffering.materials" class="course-details course-details-materials c-pipe c-pipe--block-end c-pipe--border-default c-pipe--space-md u-space-block-end-tight" style="--pipe-color: var(--card-border-color); --pipe-size: var(--size-4xs);">
                                        <p class="course-detail-materials-title"><span>{{ material.title }} </span>
                                        <span v-if="material.materialRequired" class="u-font-size-copy-sm"> - <b>Required</b></span></p>
                                        <div class="o-flex-box u-space-gap-col u-space-gap-row-sm u-flex-flow-row-wrap">
                                            <div v-if="material.author !== ''">
                                                <p class="course-detail course-detail-author">
                                                    <span class="course-detail-title">Author:</span> {{ material.author }}
                                                </p>
                                            </div>
                                            <div v-if="material.edition !== '' || material.publicationYear !== ''">
                                                <p class="course-detail course-detail-edition">
                                                    <span class="course-detail-title">Edition:</span> {{ material.edition }} <span v-if="material.publicationYear !== ''"> - {{ material.publicationYear }}</span>
                                                </p>
                                            </div>
                                            <div v-if="material.publisher !== ''">
                                                <p class="course-detail course-detail-publisher">
                                                    <span class="course-detail-title">Publisher:</span> {{ material.publisher }} 
                                                </p>
                                            </div>
                                            <div v-if="material.isbn !== ''">
                                                <p class="course-detail course-detail-isbn">
                                                    <span class="course-detail-title">ISBN:</span> {{ material.isbn }}
                                                </p>
                                            </div>
                                            <div>
                                                <p class="course-detail course-detail-price">
                                                    <span class="course-detail-title">MSRP:</span> 
                                                    <span v-if="material.price === '0.00'">N/A</span>
                                                    <span v-else>${{ material.price }}</span>
                                                </p>
                                            </div>
                                        </div>
                                        <p v-if="material.materialNotes !== ''" class="course-detail course-detail-notes"><b>Textbook/Material Note:</b> {{ material.materialNotes }}</p> 
                                    </div>
                                </div>
                                <p v-else class="u-font-size-copy-sm">Materials have not been finalized for this course</p>
                                <p class="u-font-size-copy-sm" v-if="courseOffering.classFeeAmount > 0"><b>Class Fee:</b> ${{ courseOffering.classFeeAmount }}</p>
                                <p class="u-font-size-copy-sm" v-if="courseOffering.classFeeDescription > 0"><b>Class Fee Description:</b> {{ courseOffering.classFeeDescription }}</p>
                              
                                
                                    
                                <div v-if="courseOffering.labOfferings.length > 0">
                                    <div class="u-space-block-start-loose">
                                        <h4 class="u-space-block-end-collapsed">Lab Sections</h4>
                                        <div  v-for="(labOffering, loIndex) in courseOffering.labOfferings"
                                                :key="`lab-${loIndex}`"
                                                :id="`lab-${index}-${coIndex}-${loIndex}`" class="o-card-grid o-fixed-grid o-fixed-grid--one-up u-space-block-end-loose">
                                            <div class="oux-card">      
                                                <div v-for="(localMeetingPattern, localmpIndex) in labOffering.meetingPatterns" class="course-intro u-align-items-start u-space-block-end-tight" >
                                                    <div><h5>Lab Section: {{ labOffering.section }}</h5></div>
                                                   
                                                    <div class="course-modality"
                                                        v-if="localMeetingPattern.isHybrid"
                                                        style="--bg-color: var(--ilstu-purple-21); margin-right:3em;"
                                                        >
                                                        Hybrid
                                                        </div>

                                                        <div class="course-modality"
                                                        v-else-if="localMeetingPattern.buildingFullName !== '' || localMeetingPattern.buildingFullName !== null"
                                                        style="--bg-color: var(--ilstu-coral-21); margin-right:3em;"
                                                        >
                                                        In Person
                                                        </div>

                                                        <div class="course-modality"
                                                        v-else
                                                        style="--bg-color: var(--ilstu-azul-21); margin-right:3em;"
                                                        >
                                                        Online
                                                    </div>


                                                </div>  
                                                <div class="course-outro o-flex-box u-space-gap u-space-block-end u-space-gap-col u-space-gap-row-sm u-flex-flow-row-wrap">
                                                    <div class="o-flex-box u-space-gap-xs">
                                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">frame_person</span>
                                                        <p class="u-font-size-copy-xs u-font-weight-regular" v-for="(instructor, instIndex) in labOffering.instructors">{{ instructor.firstName }} {{ instructor.lastName }}</p>
                                                    </div>
                                                    <div class="o-flex-box u-space-gap-xs">
                                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">calendar_clock</span>
                                                        <p class="u-font-size-copy-xs u-font-weight-regular" v-for="(labMeetingPattern, lmpIndex) in labOffering.meetingPatterns"><span><span>{{ labMeetingPattern.meetingDaysLongForm }} {{ formatMeetingTime(labMeetingPattern.startTime) }}-{{ formatMeetingTime(labMeetingPattern.endTime) }}</span></span></p>
                                                    </div>
                                                    <div class="o-flex-box u-space-gap-xs">
                                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">people</span>
                                                        <p class="u-font-size-copy-xs u-font-weight-regular">Enrollment: {{ labOffering.currentEnrollment }} / {{ labOffering.maxEnrollment }}</p>
                                                    </div>
                                                    <div v-if="labOffering.isMajorOnly || labOffering.isHonorsOnly || labOffering.isFreshmanOnly" class="o-flex-box u-space-gap-xs">
                                                        <span class="material-symbols-rounded" aria-hidden="true" focusable="false">school</span>
                                                        <p class="u-font-size-copy-xs u-font-weight-regular">
                                                            <span v-if="labOffering.isMajorOnly">{{ labOffering.classAttributes.MAJORONLYMAJ.description }}</span>
                                                            <span v-if="labOffering.isHonorsOnly">{{ labOffering.classAttributes.HONORSHON.description }}</span>
                                                            <span v-if="labOffering.isFreshmanOnly">{{ labOffering.classAttributes.FRESHONLYFRSH.description }}</span>
                                                        </p>
                                                    </div>  
                                                    <div class="o-flex-box u-space-gap-xs">
                                                        <p class="u-font-size-copy-xs u-font-weight-regular" v-for="(labMeetingPatternAddress, lmpaIndex) in labOffering.meetingPatterns"><a :href="'https://maps.illinoisstate.edu?loc='+ labMeetingPatternAddress.buildingAbbreviation" class="c-action-link" href="#">{{ labMeetingPatternAddress.buildingFullName }} {{ labMeetingPatternAddress.buildingRoomNumber }}</a></p>
                                                    </div>
                                                </div>
                                                <div v-for="(material, materialIndex) in labOffering.materials" class="course-details course-details-materials c-pipe c-pipe--block-end c-pipe--border-default c-pipe--space-md u-space-block-end-tight" style="--pipe-color: var(--card-border-color); --pipe-size: var(--size-4xs);">
                                                    <p class="u-space-block-end-collapsed u-font-size-copy-sm"><b>Lab Materials</b></p>
                                                    <p class="course-detail-materials-title">
                                                        <span class="u-font-size-copy-sm">{{ material.title }}</span><span v-if="material.materialRequired"> - <b>Required</b></span></p>
                                                    <div v-if="material.author !== ''">
                                                        <p class="course-detail course-detail-author">
                                                            <span class="course-detail-title">Author:</span> {{ material.author }}
                                                        </p>
                                                    </div>
                                                    <div v-if="material.edition !== '' || material.publicationYear !== ''">
                                                        <p class="course-detail course-detail-edition">
                                                            <span class="course-detail-title">Edition:</span> {{ material.edition }} <span v-if="material.publicationYear !== ''"> - {{ material.publicationYear }}</span>
                                                        </p>
                                                    </div>
                                                    <div v-if="material.publisher !== ''">
                                                        <p class="course-detail course-detail-publisher">
                                                            <span class="course-detail-title">Publisher:</span> {{ material.publisher }} 
                                                        </p>
                                                    </div>
                                                    <div v-if="material.isbn !== ''">
                                                        <p class="course-detail course-detail-isbn">
                                                            <span class="course-detail-title">ISBN:</span> {{ material.isbn }}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p class="course-detail course-detail-price">
                                                            <span class="course-detail-title">MSRP:</span> 
                                                            <span v-if="material.price === '0.00'">N/A</span>
                                                            <span v-else>${{ material.price }}</span>
                                                        </p>
                                                    </div>
                                                    <p v-if="material.materialNotes !== ''" class="u-font-size-copy-sm course-detail course-detail-notes"><b>Material Note:</b> {{ material.materialNotes }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                    </ISUAccordionDetails>
                </ISUAccordionDetailsGroup>
            </section>
        </div>
    </template>
</template>

<script setup>
import { ref, shallowRef, onMounted, markRaw, watch, computed } from 'vue';
import ISUAccordionDetailsGroup from '@shared/components/ISUAccordion/ISUAccordionDetailsGroup.vue';
import ISUAccordionDetails from '@shared/components/ISUAccordion/ISUAccordionDetails.vue';
import ISUAlert from '@shared/components/ISUAlert.vue';
import axios from 'axios';
import moment from 'moment';
import { formatMeetingTime, formatCreditHours } from '@shared/utils/editorialFormatting';
import { formatMeetingDays } from '@shared/utils/meetingFormatting';
import { initAccessibilityEnhancements, generateSectionId } from '@shared/utils/accessibility';
import '@shared/styles/v4-style.css'

const courseData = ref({});
const loading = ref(true);
const error = ref(null);

function getDate(value) {
    return moment(value).format('MMMM DD, YYYY');
}

// Auto-open section accordion if user arrived via anchor link
function openTargetSection() {
    const hash = window.location.hash;
    if (hash && hash.includes('-section-')) {
        // Wait for DOM to be fully rendered
        setTimeout(() => {
            const targetSection = document.querySelector(hash);
            if (targetSection && targetSection.tagName === 'DETAILS') {
                targetSection.open = true;
                // Scroll to the opened section for better UX
                targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 200);
    }
}

onMounted(() => {
    axios.get(window.location.pathname + "json/", {})
    .then(function(results) {
        courseData.value = results.data.data;
        loading.value = false;

        // Initialize accessibility enhancements after course data loads
        setTimeout(() => {
            initAccessibilityEnhancements();
            // Auto-open section if arrived via anchor link
            openTargetSection();
        }, 100);
    })
    .catch(function(err) {
        console.error('Error fetching course data:', err);
        error.value = err;
        loading.value = false;
    });
});

const hasSidebar = computed(() => {
  const c = courseData?.value ?? courseData
  const crossLen   = c?.crossListedCourses?.length ?? 0
  return crossLen > 0
})

</script>
